import * as pc from 'playcanvas';
import type { Building } from '../api/types';

/**
 * Loads a licensed, ready-made Gaussian-splat scan (.sog/.ply — Blueprint DR-10) as a
 * "hero building". Unlike the modular kit (src/game/kit.ts), a real-world capture can't be
 * auto-normalized to a data-given box: its axis convention and scale are specific to how it
 * was processed, so placement is a small per-asset calibration (rotation + scale), the same
 * way PlayCanvas's own gsplat examples hardcode a `sceneRotation` per capture.
 */

const splatCache = new Map<string, Promise<pc.Asset | null>>();

function loadGsplatAsset(app: pc.Application, url: string): Promise<pc.Asset | null> {
  let pending = splatCache.get(url);
  if (pending) return pending;
  pending = new Promise((resolve) => {
    const asset = new pc.Asset(url, 'gsplat', { url });
    asset.once('load', () => resolve(asset));
    asset.once('error', (err: unknown) => {
      console.warn(`[GOC splat] failed to load ${url}:`, err);
      resolve(null);
    });
    app.assets.add(asset);
    app.assets.load(asset);
  });
  splatCache.set(url, pending);
  return pending;
}

/** Loads the splat and returns an entity ready to be added to the scene and positioned. */
export async function spawnSplatBuilding(
  app: pc.Application,
  calibration: NonNullable<Building['splat']>,
): Promise<pc.Entity | null> {
  const asset = await loadGsplatAsset(app, calibration.url);
  if (!asset?.resource) return null;

  const entity = new pc.Entity('hero-splat');
  entity.addComponent('gsplat', { asset });
  entity.setLocalEulerAngles(calibration.rotation.x, calibration.rotation.y, calibration.rotation.z);
  entity.setLocalScale(calibration.scale, calibration.scale, calibration.scale);
  return entity;
}
